import React, { useContext, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";

import { InfiniteScroll } from "../../../components";
import {
  Button,
  ConfirmDialog,
  Icon,
  IconButton,
  Menu,
  SearchInput,
  StatePanel,
} from "../../../components/ui";
import { UserContext } from "../../../contexts";
import { getApiUrl } from "../../../core/config/api";
import { downloadLookbook } from "../../helpers";

// ── Inline SVG icons ──────────────────────────────────────────────────────────

// Document illustration: page with folded corner, content lines, subtle glow
const DocIllustration = ({ hasPdf }) => (
  <svg width="52" height="60" viewBox="0 0 52 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="doc-bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor="#2a1a5e"/>
        <stop offset="100%" stopColor="#1a1030"/>
      </linearGradient>
      <linearGradient id="doc-fold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%"   stopColor="#6d45c0"/>
        <stop offset="100%" stopColor="#3b1f7a"/>
      </linearGradient>
      <radialGradient id="doc-glow" cx="40%" cy="30%" r="60%">
        <stop offset="0%"   stopColor="#7145D0" stopOpacity="0.35"/>
        <stop offset="100%" stopColor="#7145D0" stopOpacity="0"/>
      </radialGradient>
    </defs>

    {/* Page body — clipped around top-right fold */}
    <path d="M4 4C4 2.9 4.9 2 6 2H36L48 14V56C48 57.1 47.1 58 46 58H6C4.9 58 4 57.1 4 56V4Z"
      fill="url(#doc-bg)" stroke="rgba(113,69,208,0.35)" strokeWidth="1"/>

    {/* Glow overlay */}
    <path d="M4 4C4 2.9 4.9 2 6 2H36L48 14V56C48 57.1 47.1 58 46 58H6C4.9 58 4 57.1 4 56V4Z"
      fill="url(#doc-glow)"/>

    {/* Folded corner — triangle */}
    <path d="M36 2L48 14H38C36.9 14 36 13.1 36 12V2Z"
      fill="url(#doc-fold)" stroke="rgba(113,69,208,0.4)" strokeWidth="0.8"/>

    {/* Fold crease highlight */}
    <line x1="36" y1="2" x2="48" y2="14" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8"/>

    {/* Content lines */}
    <rect x="11" y="22" width="20" height="2.5" rx="1.25" fill="rgba(255,255,255,0.55)"/>
    <rect x="11" y="28" width="26" height="2"   rx="1"    fill="rgba(255,255,255,0.2)"/>
    <rect x="11" y="33" width="22" height="2"   rx="1"    fill="rgba(255,255,255,0.15)"/>
    <rect x="11" y="38" width="24" height="2"   rx="1"    fill="rgba(255,255,255,0.12)"/>
    <rect x="11" y="43" width="16" height="2"   rx="1"    fill="rgba(255,255,255,0.08)"/>

    {/* PDF badge — only if has file */}
    {hasPdf && (
      <>
        <rect x="10" y="49" width="20" height="7" rx="3.5"
          fill="rgba(113,69,208,0.55)" stroke="rgba(113,69,208,0.7)" strokeWidth="0.8"/>
        <text x="20" y="54.8" textAnchor="middle"
          fontFamily="-apple-system,BlinkMacSystemFont,'Helvetica Neue',sans-serif"
          fontSize="4.8" fontWeight="700" letterSpacing="0.5" fill="#d4bbff">PDF</text>
      </>
    )}
  </svg>
);

// ── Action menu ───────────────────────────────────────────────────────────────
// Shared Fontaine Menu/IconButton — gives role="menu"/"menuitem", a named
// trigger, roving-tabindex keyboard nav and Escape/outside-click/focus-return
// for free, in place of the previous hand-rolled (mouse-only) popup.

// The PDF is rendered on a queue, so a freshly created lookbook has no
// `file_id` yet. Every action interpolates it straight into a URL, which sent
// the browser to `/api/pdfs/null` — a dead page that looks like a broken
// feature rather than "not ready yet".
const PENDING_HINT = "Still generating";

/**
 * A request lookbook renders two PDFs — network (`external_file_id`) and admin
 * (`file_id`, which predates the split) — and they differ in what money they
 * show, so nothing ever guesses which one you meant. A listing lookbook has
 * only `file_id`, and gets the plain unlabelled entry.
 */
const copiesOf = (lookbook) =>
  lookbook.external_file_id
    ? [
        { label: "Network", fileId: lookbook.external_file_id, nameSuffix: " (network)" },
        { label: "Admin", fileId: lookbook.file_id, nameSuffix: " (admin)" },
      ]
    : [{ label: "", fileId: lookbook.file_id, nameSuffix: "" }];

const viewPdf = (fileId) => window.open(getApiUrl(`/api/pdfs/${fileId}`), "_blank");

const LookbookMenu = ({ lookbook, fileName, editPath, canDelete, onDelete }) => {
  const history = useHistory();
  const copies = copiesOf(lookbook);

  const items = [
    {
      label: "Edit",
      icon: <Icon name="Pencil" size="xs" />,
      onSelect: () => history.push(editPath),
    },
    ...copies.map((copy) => ({
      label: copy.label ? `Download ${copy.label.toLowerCase()} copy` : "Download",
      icon: <Icon name="Download" size="xs" />,
      disabled: !copy.fileId,
      hint: copy.fileId ? undefined : PENDING_HINT,
      onSelect: () =>
        downloadLookbook(
          lookbook.id,
          copy.fileId,
          lookbook.data?.lookbookName
            ? `${lookbook.data.lookbookName}${copy.nameSuffix}`
            : undefined
        ),
    })),
    ...(canDelete
      ? [
          { divider: true },
          {
            label: "Delete",
            icon: <Icon name="Trash2" size="xs" />,
            danger: true,
            onSelect: onDelete,
          },
        ]
      : []),
  ];

  return (
    <Menu
      label={`${fileName} actions`}
      items={items}
      trigger={(
        <IconButton className="rc-lb-card__menu-btn" size="sm" round label={`${fileName} actions`}>
          <Icon name="MoreHorizontal" size="xs" />
        </IconButton>
      )}
    />
  );
};

// ── Lookbook card ─────────────────────────────────────────────────────────────

const LookbookCard = ({ lookbook, editPath, canDelete, onDelete }) => {
  const fileName = lookbook.data?.lookbookName
    ? lookbook.data.lookbookName
    : `LOOKBOOK_S${String(lookbook.id).padStart(3, "0")}`;

  const clientName = lookbook?.assigned_to
    ? `${lookbook.assigned_to.first_name ?? ""} ${lookbook.assigned_to.last_name ?? ""}`.trim()
    : null;

  const dateStr = lookbook.created_at
    ? new Date(lookbook.created_at).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      })
    : null;

  const hasPdf = !!lookbook.file_id;

  // The card itself is not a button any more: clicking it opened the EDIT
  // flow, which is rarely what anyone wanted from a finished lookbook. Viewing
  // is the explicit buttons under the illustration; Edit lives in the menu.
  return (
    <div className="rc-lb-card">
      <div className="rc-lb-card__doc">
        {/* Decorative — name/date/client already say it all */}
        <DocIllustration hasPdf={hasPdf} />
        <div className="rc-lb-card__views">
          {copiesOf(lookbook).map((copy) => (
            <Button
              key={copy.label || "view"}
              size="sm"
              variant="secondary"
              className="rc-lb-card__view"
              leadingIcon={<Icon name="Eye" size="xs" />}
              disabled={!copy.fileId}
              title={copy.fileId ? undefined : PENDING_HINT}
              aria-label={`View ${copy.label ? `${copy.label.toLowerCase()} copy of ` : ""}${fileName}`}
              onClick={() => viewPdf(copy.fileId)}
            >
              {copy.label || "View"}
            </Button>
          ))}
        </div>
      </div>

      <div className="rc-lb-card__info">
        <span className="rc-lb-card__name" title={fileName}>{fileName}</span>
        {clientName && (
          <span className="rc-lb-card__client">{clientName}</span>
        )}
        {dateStr && (
          <span className="rc-lb-card__date">{dateStr}</span>
        )}
      </div>

      <div className="rc-lb-card__actions">
        <LookbookMenu
          lookbook={lookbook}
          fileName={fileName}
          editPath={editPath}
          canDelete={canDelete}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
};

// ── Library (search + grid) ───────────────────────────────────────────────────

/**
 * The lookbook library: search box over a card grid.
 *
 * Shared by the two lookbook flavours — listing lookbooks (Listings →
 * Lookbooks) and request lookbooks (Orders → Lookbooks) — so the two pages are
 * the same page with a different `type` and a different place to edit. Anything
 * that changes the card, the menu or the empty state changes both.
 *
 * @param {"listing"|"request"} type          which flavour to list
 * @param {(lookbook) => string} getEditPath  full path (query string included)
 *                                            that "Edit" and a card click go to
 * @param {string} [emptyDescription]         copy for the never-created state
 */
export const LookbookLibrary = ({ type, getEditPath, emptyDescription }) => {
  const [query, setQuery] = useState("");
  const { user } = useContext(UserContext);
  // DELETE /api/lookbooks/{id} lets an admin delete any lookbook; anyone else
  // only automated-test rows. So the action is shown to admins only, rather
  // than offered to everyone and refused.
  const canDelete = !!user?.is_admin;
  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  // Deleted ids are hidden in place — re-fetching would reset the infinite
  // scroll to page one and lose the reader’s position.
  const [removed, setRemoved] = useState(() => new Set());

  const closeConfirm = () => {
    if (deleting) return;
    setConfirming(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!confirming) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(`/api/lookbooks/${confirming.id}`, { metadata: { quiet: true } });
      setRemoved((previous) => new Set(previous).add(confirming.id));
      setConfirming(null);
    } catch (e) {
      setDeleteError("Couldn’t delete this lookbook. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      {/* ── Filter bar ── */}
      <div className="rc-lookbooks__filterbar">
        <SearchInput
          size="lg"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery("")}
          placeholder="Search lookbooks"
        />
      </div>

      {/* ── Grid ── */}
      <div className="rc-lookbooks__grid-wrap">
        <InfiniteScroll
          fetchUrl="/api/lookbooks/list"
          fetchParams={{ query, type }}
          emptyMessage=""
          onResultsLoaded={() => {}}
          fetchLimit={24}
        >
          {({ items: loaded, isLoading }) => {
            const items = loaded.filter((lb) => !removed.has(lb.id));

            return (
              <>
                {items.length > 0 && (
                  <div className="rc-lookbooks__grid">
                    {items.map((lb) => (
                      <LookbookCard
                        key={lb.id}
                        lookbook={lb}
                        editPath={getEditPath(lb)}
                        canDelete={canDelete}
                        onDelete={() => setConfirming(lb)}
                      />
                    ))}
                  </div>
                )}
                {!isLoading && items.length === 0 && (
                  <div className="rc-lookbooks__empty">
                    <StatePanel
                      state={query ? "no-results" : "empty"}
                      title={query ? undefined : "No lookbooks yet"}
                      description={query ? undefined : emptyDescription}
                    />
                  </div>
                )}
              </>
            );
          }}
        </InfiniteScroll>
      </div>

      <ConfirmDialog
        open={!!confirming}
        onClose={closeConfirm}
        onConfirm={confirmDelete}
        title="Delete lookbook?"
        description={
          confirming
            ? `“${confirming.data?.lookbookName || `LOOKBOOK_S${String(confirming.id).padStart(3, "0")}`}” will be deleted for everyone with access to it.`
            : undefined
        }
        confirmLabel="Delete"
        danger
        loading={deleting}
      >
        {deleteError && <p className="rc-lookbooks__delete-error" role="alert">{deleteError}</p>}
      </ConfirmDialog>
    </>
  );
};

export default LookbookLibrary;
